import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  findClinicsByIds,
  getPatientWidget,
  ScreenMap,
  Screens,
  Theme,
  ThemeMap,
  BookingConfig,
  findAvailableTreatmentIdsPerClinic,
} from 'patient-api-lib'
import _merge from 'lodash.merge'
import { AppDispatch, RootState } from '../store'
import {
  IntegrationType,
  MessageType,
  OnlineDentistKeys,
  WidgetMode,
} from '../models/enums'
import { City, Config, Clinic, Language, ErrorObject } from '../models/types'
import { DateTime } from 'luxon'
import {
  setSelectedClinic,
  updateUpcomingAppointments,
} from './clinicScreen/clinicScreenSlice'
import { fetchCityLocation, setCity } from './cityScreenSlice'
import {
  filterTreatments,
  setSelectedTreatment,
} from './treatmentScreen/treatmentScreenSlice'
import { getTreatmentIdsFromCategories } from './treatmentScreen/treatmentUtils'
import { LANGUAGE } from '../locales/config'
import { setNearestAppointmentDate } from './appointmentScreen/appointmentScreenSlice'
import { setIsRebooking } from './bookingFlow/bookingFlowSlice'
import { getWindow } from '../util/windowUtils'
import { clearState as clearAppointmentState } from '../features/appointmentScreen/appointmentScreenSlice'
import { clearState as clearContactInformationState } from '../features/contactInformationScreenSlice'
import { clearState as clearClinicState } from '../features/clinicScreen/clinicScreenSlice'
import { clearState as clearTreatmentState } from '../features/treatmentScreen/treatmentScreenSlice'
import { navigateBackTo } from './screensRouterSlice'
import { setCorrectClinicCity } from './clinicScreen'

interface AppConfigState extends Config {
  cities: City[]
  clinics: Clinic[]
  startup: boolean
  loadConfig: boolean
  loadConfigError: ErrorObject | null
  apiToken: string | null
}

export const initialState: AppConfigState = {
  mode: null,
  cities: [],
  clinics: [],
  themeSettings: {
    colors: undefined,
  },
  apiToken: null,
  startup: true,
  loadConfig: true,
  loadConfigError: null,
  initialScreen: Screens.CityScreen,
  theme: Theme.Default,
  customerServicePhone: '',
  privacyPolicyUrl: '',
  integrationType: null,
  clinicIds: [],
  treatmentIds: [],
  treatmentCategories: [],
  language: LANGUAGE.SV,
}

export const fetchWidgetConfig = createAsyncThunk<
  void,
  Partial<Config>,
  { dispatch: AppDispatch; state: RootState }
>('appConfig/fetchWidgetConfig', async (reassignConfig, thunkApi) => {
  if (reassignConfig.mode) {
    thunkApi.dispatch(setWidgetMode(reassignConfig.mode))
  }

  try {
    const responseConfig = await getPatientWidget()
    const config = responseConfig.getBookingConfig()

    if (config) {
      const configClinicIds = config.getClinicIdsList().map((id) => id)

      if (reassignConfig.clinicIds) {
        reassignConfig.clinicIds = reassignConfig.clinicIds.filter((id) =>
          configClinicIds.includes(id)
        )
      }

      if (reassignConfig.treatmentIds) {
        reassignConfig.treatmentIds = reassignConfig.treatmentIds.filter((id) =>
          config
            .getTreatmentGroupsList()
            .some((group) => group.getTreatmentIdsList().includes(id))
        )
      }

      if (
        reassignConfig.clinicIds?.length === 0 &&
        reassignConfig.treatmentIds?.length === 0
      ) {
        return thunkApi.rejectWithValue({
          message: 'Clinic id`s and treatment id`s in url is invalid',
          showButton: false,
        })
      }

      if (reassignConfig.clinicIds?.length === 0) {
        return thunkApi.rejectWithValue({
          message: 'Clinic id`s in url is invalid',
          showButton: false,
        })
      }

      if (reassignConfig.treatmentIds?.length === 0) {
        return thunkApi.rejectWithValue({
          message: 'Treatment id`s in url is invalid',
          showButton: false,
        })
      }

      if (!reassignConfig.apiToken) {
        return thunkApi.rejectWithValue({
          message: 'apiToken is undefined',
          showButton: false,
        })
      }

      const newConfig: Config = {
        mode: reassignConfig.mode,
        apiToken: reassignConfig.apiToken,
        theme:
          reassignConfig.theme ||
          (ThemeMap[
            config.getTheme()?.getPreset() as BookingConfig.Theme.Preset
          ] as Theme),
        themeSettings: {
          colors: config
            .getTheme()
            ?.getColors()
            ?.toObject() as BookingConfig.Theme.Colors.AsObject,
        },
        images: {
          logotype: config
            .getTheme()
            ?.getImagesList()
            .reduce((url, image) => {
              if (
                image.getType() === BookingConfig.Theme.Image.Type.TYPE_LOGO
              ) {
                return image.getSrc()
              }

              return url
            }, ''),
        },
        jumbotron: {
          bullets: config
            .getTheme()
            ?.getJumbotronBulletsList()
            .map((bullet) => bullet),
          title: config.getTheme()?.getJumbotronTitle()?.toString(),
          image: config
            .getTheme()
            ?.getImagesList()
            .reduce((url, image) => {
              if (
                image.getType() ===
                BookingConfig.Theme.Image.Type.TYPE_JUMBOTRON
              ) {
                return image.getSrc()
              }

              return url
            }, ''),
        },
        initialScreen: ScreenMap[config.getInitialScreen()],
        language: config.getLanguage()?.getValue() as Language,
        customerServicePhone:
          config.getCustomerServicePhone()?.getE164Number() || '',
        clinicIds: configClinicIds,
        privacyPolicyUrl: config.getPrivacyPolicyUrl()?.getValue() || '',
        treatmentCategories: config.getTreatmentGroupsList().map((group) => {
          return {
            name: group.getName() || '',
            description: group.getDescription() || '',
            extendedDescription:
              group.getExtendedDescription()?.getValue() || '',
            treatmentsList: group.getTreatmentsList().map((treatment) => {
              return {
                id: treatment.getId(),
                name: treatment.getName()?.getValue() || '',
                description: treatment.getDescription()?.getValue() || '',
                extendedDescription:
                  treatment.getExtendedDescription()?.getValue() || '',
              }
            }),
          }
        }),
        ...reassignConfig,
      }

      await thunkApi.dispatch(
        setConfig({
          ...newConfig,
        })
      )

      thunkApi.dispatch(fetchClinicsData())
    }

    return undefined
  } catch (error: unknown) {
    const err = error as Error

    return thunkApi.rejectWithValue({
      message: err?.message,
      showButton: true,
    } as ErrorObject)
  }
})

export const fetchClinicsData = createAsyncThunk<
  { initialScreen: Screens },
  boolean | undefined,
  { dispatch: AppDispatch; state: RootState }
>('appConfig/fetchClinicsData', async (skipRedirects, thunkApi) => {
  try {
    const overrideInitialState: { initialScreen: Screens } = {
      initialScreen: thunkApi.getState().app.initialScreen,
    }
    const {
      clinicIds,
      treatmentIds: treatmentIdsFilter,
      treatmentCategories: treatmentCategoriesFromConfig,
    } = thunkApi.getState().app
    const cities: { [name: string]: City } = {}
    const clinics: Clinic[] = []
    const myBookingsState = thunkApi.getState().myBookingsScreen
    // For my bookings flow
    if (myBookingsState.jwt) {
      thunkApi.dispatch(setIsRebooking(true))
      overrideInitialState.initialScreen = Screens.YourBookingsScreen

      if (!skipRedirects) {
        return overrideInitialState
      }
    }

    const treatmentIds = getTreatmentIdsFromCategories(
      treatmentCategoriesFromConfig,
      treatmentIdsFilter
    )

    const startDate = DateTime.now()
    const endDate = startDate.plus({ month: 3 })

    const clinicAvailabilityList = await findAvailableTreatmentIdsPerClinic(
      clinicIds,
      startDate.toJSDate(),
      endDate.toJSDate()
    )

    const clinicIdsWithAvailableTreatments = [
      ...new Set(
        clinicAvailabilityList
          .getClinicsList()
          .filter((t) =>
            t.getTreatmentIdsList().some((ct) => treatmentIds.includes(ct))
          )
          .flatMap((c) => c.getClinicId())
      ),
    ]

    const availableUpcomingTreatments = [
      ...new Set(
        clinicAvailabilityList
          .getClinicsList()
          .flatMap((c) => c.getTreatmentIdsList())
          .filter((t) => treatmentIds.includes(t))
      ),
    ]

    const findClinicsResponse = await findClinicsByIds(clinicIds)

    findClinicsResponse.getClinicsList().forEach(async (clinic) => {
      if (!clinic) return

      const clinicId = clinic.getId()
      const clinicTreatments = clinic.getTreatmentsList()
      const clinicAddress = clinic.getVisitingAddress()
      const clinicLocation = clinic.getLatLng()
      const clinicPhone = clinic.getTelephone()?.getE164Number()

      // Case for Online Dentist
      if (clinicId !== OnlineDentistKeys.id) {
        if (!clinicAddress || !clinicLocation)
          throw new Error('Clinic address and location is missing')
      }

      const clinicLocality = clinicAddress
        ? setCorrectClinicCity(clinicAddress.getLocality())
        : ''

      let availableTreatmentInCity = clinicTreatments
        .filter((t) => availableUpcomingTreatments.includes(t.getTreatmentId()))
        .map((t) => t.getTreatmentId())

      // Case for Online Dentist
      let availableTreatmentCount = clinicIdsWithAvailableTreatments.includes(
        clinicId
      )
        ? availableTreatmentInCity.length
        : 0

      // Case for Online Dentist
      if (clinicIdsWithAvailableTreatments.includes(OnlineDentistKeys.id)) {
        const onlineDentistTreatments = clinics.find(
          (c) => c.id === OnlineDentistKeys.id
        )?.treatmentIds

        const onlineDentistTreatmentIds = onlineDentistTreatments?.filter((t) =>
          availableUpcomingTreatments.includes(t)
        )

        availableTreatmentInCity = [
          ...availableTreatmentInCity,
          ...(onlineDentistTreatmentIds ?? []),
        ]
        availableTreatmentCount =
          (onlineDentistTreatmentIds?.length ?? 0) + availableTreatmentCount
      }

      cities[clinicLocality] = _merge<City, City>(cities[clinicLocality], {
        clinicIds: [...(cities[clinicLocality]?.clinicIds ?? []), clinicId],
        name: clinicAddress ? clinicLocality : '',
        treatmentIds: availableTreatmentInCity,
        availableTreatments: availableTreatmentCount,
      })

      clinics.push({
        id: clinic.getId(),
        name: clinic.getName().replace('Aqua Dental ', ''),
        city: setCorrectClinicCity(clinicLocality),
        subCity: clinicAddress ? clinicAddress.getLocality() : '',
        treatmentIds: clinicTreatments.map((t) => t.getTreatmentId()),
        address: `${
          clinicAddress?.getAddressLinesList()[0]
        }, ${clinicAddress?.getPostalCode()} ${clinicAddress?.getLocality()}`,
        latLng: {
          latitude: clinicLocation ? clinicLocation.getLatitude() : 0,
          longitude: clinicLocation ? clinicLocation.getLongitude() : 0,
        },
        phoneNumber: clinicPhone,
        distance: null,
      })
    })

    const citiesAsArray = Object.values(cities)

    // If list of cities is only one, set that as the selected city and skip the city screen
    if (citiesAsArray.length === 1) {
      const oneCity = citiesAsArray[0]
      thunkApi.dispatch(setCity(oneCity))

      await thunkApi.dispatch(
        updateUpcomingAppointments({
          clinicId: clinics[0].id,
          pageSize: 1000,
        })
      )
      if (!oneCity.latLng && oneCity?.name) {
        await thunkApi.dispatch(fetchCityLocation(oneCity?.name))
      }

      if (clinics && clinics.length === 1) {
        thunkApi.dispatch(setSelectedClinic(clinics[0]))
      }

      overrideInitialState.initialScreen = Screens.TreatmentScreen
    }

    // If list of clinics is only one
    if (clinics && clinics.length === 1) {
      await thunkApi.dispatch(filterTreatments())
    }

    // If list of treatments is only one, set that as the treatment and skip the treatment screen
    if (treatmentIds?.length === 1) {
      const singleTreatmentPreDefined = treatmentCategoriesFromConfig
        .map((t) => t.treatmentsList)
        .flat()
        .find((t) => t.id === treatmentIds[0])

      if (!singleTreatmentPreDefined)
        throw new Error('Treatment in categories not found')

      thunkApi.dispatch(setSelectedTreatment(singleTreatmentPreDefined))

      await thunkApi.dispatch(
        updateUpcomingAppointments({
          clinicId: clinics[0].id,
          pageSize: 1000,
        })
      )

      const updatedClinicList = thunkApi.getState().clinicScreen.clinics
      thunkApi.dispatch(setNearestAppointmentDate(updatedClinicList[0]))

      if (treatmentIds.length === 1 && singleTreatmentPreDefined) {
        overrideInitialState.initialScreen = Screens.ClinicScreen

        if (clinics.length === 1) {
          overrideInitialState.initialScreen = Screens.AppointmentScreen
        }
      }
    }

    const cityList = Object.keys(cities).map((area) => ({
      name: area,
      availableTreatments:
        citiesAsArray.find((c) => c.name === area)?.availableTreatments ?? 0,
      treatmentIds: citiesAsArray.find((c) => c.name === area)?.treatmentIds,
      clinicIds: citiesAsArray.find((c) => c.name === area)?.clinicIds,
    }))

    thunkApi.dispatch(setCities(cityList))
    thunkApi.dispatch(setClinics(Object.values(clinics)))

    return overrideInitialState
  } catch (error: unknown) {
    const err = error as Error

    return thunkApi.rejectWithValue({
      message: err?.message,
      showButton: true,
    } as ErrorObject)
  }
})

export const callException = createAsyncThunk<
  void,
  ErrorObject,
  { dispatch: AppDispatch; state: RootState }
>('appConfig/callException', async (errorObject, thunkApi) => {
  return thunkApi.rejectWithValue({
    ...errorObject,
  })
})

export const restartApp = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>('appConfig/restartApp', async (_, thunkApi) => {
  const hostWindow = getWindow()
  // send message of end of booking flow
  hostWindow?.postMessage({ type: MessageType.Close }, '*')
  thunkApi.dispatch(clearAppointmentState())
  thunkApi.dispatch(clearContactInformationState())
  thunkApi.dispatch(clearClinicState())
  thunkApi.dispatch(clearTreatmentState())
  // navigate to the start screen
  await thunkApi.dispatch(fetchClinicsData()).then((resp) => {
    if (resp.meta.requestStatus === 'fulfilled') {
      const payload = resp.payload as { initialScreen: Screens }
      thunkApi.dispatch(navigateBackTo(payload.initialScreen))
    }
  })
})

export const appConfigSlice = createSlice({
  name: 'appConfig',
  initialState,
  reducers: {
    setTheme: (state, action: PayloadAction<Theme>) => {
      state.theme = action.payload
    },
    setConfig: (state, action: PayloadAction<Config>) => {
      const {
        theme,
        initialScreen,
        language,
        customerServicePhone,
        images,
        treatmentCategories,
      } = _merge({ ...state }, action.payload)
      const {
        clinicIds,
        jumbotron,
        treatmentIds,
        apiToken,
        integrationType,
        bookingId,
        privacyPolicyUrl,
      } = action.payload

      state.treatmentIds = treatmentIds
      state.jumbotron = jumbotron
      state.images = images
      state.theme = theme
      state.initialScreen = initialScreen
      state.language = language
      state.treatmentCategories = treatmentCategories
      state.privacyPolicyUrl = privacyPolicyUrl
      state.customerServicePhone = customerServicePhone
      state.clinicIds = clinicIds
      state.apiToken = apiToken
      state.integrationType = integrationType
      state.bookingId = bookingId
    },
    setWidgetMode: (state, action: PayloadAction<WidgetMode>) => {
      state.mode = action.payload
    },
    setCities: (state, action: PayloadAction<City[]>) => {
      state.cities = [...action.payload]
    },
    setClinics: (state, action: PayloadAction<Clinic[]>) => {
      state.clinics = [...action.payload]
    },
    setBookingId: (state, action: PayloadAction<string | null>) => {
      state.bookingId = action.payload
    },
    setError: (state, action: PayloadAction<ErrorObject | null>) => {
      state.loadConfigError = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchWidgetConfig.rejected, (state, response) => {
      state.startup = false
      state.loadConfig = false
      state.loadConfigError = response.payload as ErrorObject
    })
    builder.addCase(fetchClinicsData.pending, (state, payload) => {
      state.startup = !payload.meta.arg
      state.loadConfigError = null
    })
    builder.addCase(fetchClinicsData.rejected, (state, response) => {
      state.startup = false
      state.loadConfig = false
      state.loadConfigError = response.payload as ErrorObject
    })
    builder.addCase(fetchClinicsData.fulfilled, (state, action) => {
      state.initialScreen = action.payload.initialScreen
      state.startup = false
      state.loadConfig = false
      state.loadConfigError = null
    })
    builder.addCase(restartApp.pending, (state) => {
      state.startup = true
    })
    builder.addCase(restartApp.fulfilled, (state) => {
      state.startup = false
    })
    builder.addCase(restartApp.rejected, (state) => {
      state.startup = false
    })
    builder.addCase(callException.rejected, (state, response) => {
      state.loadConfigError = response.payload as ErrorObject
    })
  },
})

export const {
  setTheme,
  setConfig,
  setCities,
  setClinics,
  setWidgetMode,
  setError,
  setBookingId,
} = appConfigSlice.actions

export const themeSettings = (state: RootState) => state.app.themeSettings
export const selectTheme = (state: RootState) => state.app.theme
export const selectMode = (state: RootState) => state.app.mode
export const selectLanguage = (state: RootState) => state.app.language
export const selectInitialScreen = (state: RootState) => state.app.initialScreen
export const selectCustomerServicePhone = (state: RootState) =>
  state.app.customerServicePhone
export const selectPrivacyPolicyUrl = (state: RootState) =>
  state.app.privacyPolicyUrl
export const selectCities = (state: RootState) => state.app.cities
export const selectClinics = (state: RootState) => state.app.clinics
export const getLogotype = (state: RootState) => state.app.images?.logotype
export const getStartup = (state: RootState) => state.app.startup
export const getSidebarImage = (state: RootState) => state.app.jumbotron?.image
export const getSidebarTitle = (state: RootState) => state.app.jumbotron?.title
export const getSidebarBullets = (state: RootState) =>
  state.app.jumbotron?.bullets
export const loadConfig = (state: RootState) => state.app.loadConfig
export const loadConfigError = (state: RootState) => state.app.loadConfigError
export const selectApiToken = (state: RootState) => state.app.apiToken
export const selectIntegrationType = (state: RootState) =>
  state.app.integrationType
export const checkIsPatientHub = (state: RootState) =>
  state.app.integrationType === IntegrationType.PatientHub

export default appConfigSlice.reducer
